import type { Column } from 'api/shared/models';
import type { Catalog } from 'commons/modules/service/catalogsV2';
import { createOption } from 'utils/form';
import { arrayValidation } from './validation';

export const ascSort = (arr: Array<any>, prop: string) => {
  return arr.sort((a, b) => {
    return a[prop] > b[prop] ? 1 : -1;
  });
};

export const sortArrayOfAlphaNum = (data: Array<any>, property: string) => {
  if (!arrayValidation(data)) return [];
  if (!data.some((row) => row[property])) {
    return data;
  }
  const regexA = /[^a-zA-Z]/g;
  const regexN = /[^0-9]/g;

  return [...data].sort((a, b) => {
    const aA = a[property].replace(regexA, '');
    const bA = b[property].replace(regexA, '');
    if (aA === bA) {
      const aN = parseInt(a[property].replace(regexN, ''), 10);
      const bN = parseInt(b[property].replace(regexN, ''), 10);
      if (aN === bN) {
        return 0;
      }
      if (aN > bN) {
        return 1;
      }
      return -1;
    }
    return aA > bA ? 1 : -1;
  });
};

const compareMultipleColumns = <T extends Record<string, any>>(
  a: T,
  b: T,
  sortColumns: Column[],
) => {
  for (const column of sortColumns) {
    if (!column.apiField || !column.direction) continue;

    const aValue = a[column.apiField as keyof T];
    const bValue = b[column.apiField as keyof T];

    // Manejar valores nulos
    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    let comparison = 0;

    // Comparar según el tipo de dato
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      comparison = aValue.localeCompare(bValue);
    } else if (typeof aValue === 'number' && typeof bValue === 'number') {
      comparison = aValue - bValue;
    } else {
      comparison = String(aValue).localeCompare(String(bValue));
    }

    // Si hay diferencia, retornar el resultado
    if (comparison !== 0) {
      return column.direction === 'asc' ? comparison : -comparison;
    }
  }

  // Si todas las columnas son iguales, mantener el orden original
  return 0;
};

// Función genérica para ordenar cualquier array de objetos
export const sortArrayByMultipleColumns = <T extends Record<string, any>>(
  array: T[],
  sortColumns: Column[],
): T[] => {
  if (!array || array.length === 0) return array;

  return [...array].sort((a, b) =>
    compareMultipleColumns<T>(a, b, sortColumns),
  );
};

export const sortBankers = (arr: Array<Catalog>) =>
  arr
    .map((i) =>
      createOption({
        label: `${i.value01} ${i.value02}`,
        value: i.code01,
        sequenceId: i.sequenceId || 0,
      }),
    )
    .sort((a, b) => a.label.localeCompare(b.label));

export const sortTypeValue = (arr: Array<Catalog>) =>
  arr
    .map((i) => ({
      ...createOption({
        label: i.value01 || '',
        value: i.code01 || '',
        description: i.value02 || '',
      }),
      marketType: i.code02,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

export const sort = (arr: Array<any>, refactor = false) =>
  refactor
    ? arr
        .map((i) =>
          createOption({
            label: i.value01 || '',
            value: i.code01 || '',
            description: i.value02 || '',
          }),
        )
        .sort((a, b) => a.label.localeCompare(b.label))
    : arr.sort((a, b) =>
        String(a.value01 || '').localeCompare(b.value01 || ''),
      );

export const sortLocale = (
  arr: Array<Catalog>,
  locale = 'México',
  refactor: boolean,
) => {
  const currentLocale = arr.find(
    (n) =>
      String(n.value01).toLocaleLowerCase() ===
      String(locale).toLocaleLowerCase(),
  );

  const removeCurrent = arr.filter((n) => n.value01 !== currentLocale?.value01);

  const sortCurrent = sort(removeCurrent);

  // Solo añadir currentLocale si existe
  if (currentLocale) {
    sortCurrent.unshift(currentLocale);
  }

  return refactor
    ? sortCurrent.map((i) =>
        createOption({
          label: i.value01 || '',
          value: i.code01 || '',
        }),
      )
    : sortCurrent;
};
